import useVuelidate from "@vuelidate/core";
import { email, helpers, minValue, required, requiredIf, url } from "@vuelidate/validators";
import { storeToRefs } from "pinia";
import { SettingScopesEnum } from "@simbelapp/order-sdk";
import { useCatalogStore } from "~/store/catalog/catalog.store";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { useTrainingsStore } from "~/store/trainings/trainings.store";
import type { IFooterActions } from "~/utils/interfaces/common-interfaces";
import type { ITrainingSessions } from "~/utils/interfaces/trainings-interface";
import type { IHeaderNav, IIndexHeader } from "~/utils/interfaces/table-interfaces";
import { useCreateBuilderTrainingStore } from "~/store/trainings/create-training-builder.store";
import { isCapacityRangeValid } from "~/composables/trainings/useTrainingCatalogSessions";
import { TrainingTypeEnum, VisibleStatus } from "~/utils/enums/training.enums";

/**
 * The rules « Ajouter une session » is gated on, for whichever catalogue flow opened it: the
 * wizard is one component, so an external and an internal training must refuse the same drafts.
 * `sessions` is the store slice; both stores hold the same one.
 */
export function addSessionRules(sessions: ITrainingSessions) {
  const isAddingSession = sessions.showAddSession as boolean;

  return {
    sessions: {
      sessionDraft: {
        sessionItemType: {
          required: helpers.withMessage("Vous devez sélectionner un type d’inscription.", requiredIf(isAddingSession)),
        },
        maxNbAttendees: {
          aboveMinNbAttendees: helpers.withMessage(
            "La capacité maximale doit être supérieure ou égale à la capacité minimale.",
            (max: number | null | undefined) =>
              !isAddingSession || isCapacityRangeValid(max, sessions.sessionDraft?.minNbAttendees),
          ),
        },
      },
    },
  };
}

export const useTrainings = () => {
  const headers = useState<IIndexHeader[]>(() => [
    {
      name: "Formation",
      sort: true,
      key: "title",
      minWidthPx: 208,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.SimbelCatalogTab,
    },
    {
      name: "Prestataire",
      sort: true,
      key: "training_partner_name",
      minWidthPx: 130,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.SimbelCatalogTab,
    },
    {
      name: "Catégorie",
      sort: true,
      key: "categories",
      minWidthPx: 100,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.SimbelCatalogTab,
    },
    {
      name: "Type",
      sort: true,
      key: "session_items_types",
      minWidthPx: 68,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.SimbelCatalogTab,
    },
    {
      name: "Prix (HT)",
      sort: true,
      key: "default_price",
      minWidthPx: 80,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.SimbelCatalogTab,
    },
    {
      name: "Format",
      sort: true,
      key: "format",
      minWidthPx: 84,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.SimbelCatalogTab,
    },
    {
      name: "Durée",
      sort: true,
      key: "duration_hours",
      minWidthPx: 66,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.SimbelCatalogTab,
    },
    {
      name: "",
      sort: false,
      key: "actions",
      minWidthPx: 64,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.SimbelCatalogTab,
    },
    {
      name: "Référent",
      sort: true,
      key: "training_admin",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.SimbelCatalogTab,
    },
  ]);

  const footersActionsSimbel: IFooterActions[] = [
    {
      name: "Ajouter à mon répertoire",
      logoName: "add-library",
      action: addTrainingsToCatalog,
    },
  ];
  function addTrainingTag() {
    showEditTagModal.value = true;
  }
  const footersActionsCatalog: IFooterActions[] = [
    {
      name: "Tag formation",
      logoName: "categorie",
      action: addTrainingTag,
    },
    {
      name: "Gérer la visibilité",
      logoName: "see",
      action: showMultiVisibility,
    },
    {
      name: "Archiver",
      logoName: "delete_trash",
      action: deleteTrainingsFromCatalog,
    },
  ];

  const catalogStore = useCatalogStore();
  const { showEditTagModal } = storeToRefs(catalogStore);

  function showMultiVisibility() {
    catalogStore.showVisibilityModal = true;
  }

  function deleteTrainingsFromCatalog() {
    const catalogsStore = useCatalogStore();

    catalogsStore.showDeleteCatalog = true;
  }

  function addTrainingsToCatalog() {
    const trainingsStore = useTrainingsStore();

    trainingsStore.showAddCatalog = true;
  }

  function getNav(isEdit?: boolean): IHeaderNav[] {
    const nav = [
      { name: "Général", nb: 0 },
      { name: "Contenu", nb: 1 },
      { name: "Inscriptions", nb: 2 },
      { name: "Modalités", nb: 3 },
    ];
    if (isEdit) {
      nav.push({
        name: "Sessions",
        nb: 4,
      });
    }

    return nav;
  }

  function getBuilderNav(isEdit?: boolean): IHeaderNav[] {
    const nav = [
      { name: "Contenu", nb: 0 },
      { name: "Modalités", nb: 1 },
      { name: "Informations avancées", nb: 2 },
    ];
    if (isEdit) {
      nav.push({
        name: "Sessions",
        nb: 3,
      });
    }

    return nav;
  }

  function trainingTypeMap(value: string) {
    return [
      {
        name: "external_training",
        value: "Externe",
        type: "grey",
        icon: "graduation_cap_on",
      },
      {
        name: "internal_training",
        value: "Interne",
        type: "grey",
        icon: "graduation_cap_on_v2",
      },
    ].find((x) => x.name === value);
  }

  return {
    headers,
    footersActionsSimbel,
    footersActionsCatalog,
    getNav,
    getBuilderNav,
    trainingTypeMap,
  };
};

export const useTrainingValidation = () => {
  async function validateStepSessionItem() {
    const createTrainingStore = useCreateTrainingStore();
    const { training } = storeToRefs(createTrainingStore);

    function somethingIsSelected(_: any, siblings: any) {
      return siblings.sessionItems.individual.isChecked || siblings.sessionItems.group.isChecked;
    }

    const rules = computed(() => {
      return {
        sessionItems: {
          ...(training.value.sessionItems?.group.isChecked &&
            training.value.general.type === TrainingTypeEnum.EXTERNAL && {
              group: {
                modality: {},
                priceExclTax: {
                  required: helpers.withMessage("Ce champ est obligatoire", required),
                },
                type: { required },
                maxNbAttendees: {
                  required: helpers.withMessage("Veuillez saisir au moins 1", required),
                  ...(training.value.sessionItems.group.minNbAttendees && {
                    minValue: helpers.withMessage(
                      `La valeur ne doit pas être inférieur à ${training.value.sessionItems.group.minNbAttendees}`,
                      minValue(training.value.sessionItems.group.minNbAttendees),
                    ),
                  }),
                  minValue: helpers.withMessage(`Veuillez saisir au moins 1`, minValue(1)),
                },
                isChecked: { required },
              },
            }),
          ...(training.value.sessionItems?.individual.isChecked && {
            individual: {
              modality: {},
              priceExclTax: {
                required: helpers.withMessage("Ce champ est obligatoire", required),
              },
              type: { required },
              isChecked: { required },
            },
          }),
          required: helpers.withMessage("Vous devez sélectionner un type d’inscription.", somethingIsSelected),
        },
      };
    });

    const si$ = useVuelidate(rules, training.value);
    await si$.value.$validate();
    createTrainingStore.pushValidationErrors(si$.value.$errors);
  }

  async function validateStepGeneral() {
    const createTrainingStore = useCreateTrainingStore();
    const { training } = storeToRefs(createTrainingStore);

    const rules = computed(() => {
      return {
        general: {
          trainingPartner: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
          title: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
          format: {
            required: helpers.withMessage("Vous devez sélectionner un format", required),
          },
          mainContactEmail: {
            email: helpers.withMessage("Email invalide", email),
          },
          ...(training.value.general.type === TrainingTypeEnum.EXTERNAL && {
            externalUrl: {
              required: helpers.withMessage("Lien invalide", url),
            },
          }),
          ...(training.value.general.type === TrainingTypeEnum.INTERNAL && {
            internalUrl: {
              required: helpers.withMessage("Lien invalide", url),
            },
          }),
          categories: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
          durationHours: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
          durationDays: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
          ...(training.value.general.visibility.visible === VisibleStatus.GROUPS &&
            !training.value.general.visibility.groups.length && {
              visibility: {
                groups: {
                  required: helpers.withMessage("Ce champ est obligatoire", required),
                },
              },
            }),
        },
      };
    });
    const si$ = useVuelidate(rules, training.value);
    await si$.value.$validate();

    createTrainingStore.pushValidationErrors(si$.value.$errors);
  }

  async function validateStepContent() {
    const createTrainingStore = useCreateTrainingStore();
    const { training } = storeToRefs(createTrainingStore);

    const rules = computed(() => {
      return {
        content: {
          language: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
          habilitation: {
            required: helpers.withMessage(
              "Ce champ est obligatoire",
              requiredIf(training.value.content.isHabilitation),
            ),
          },
          durationYears: {
            required: helpers.withMessage(
              "Ce champ est obligatoire",
              requiredIf(training.value.content.isHabilitation),
            ),
            minValue: helpers.withMessage("La durée doit être d'au moins 1 an", minValue(1)),
          },
        },
      };
    });
    const si$ = useVuelidate(rules, training.value);
    await si$.value.$validate();
    createTrainingStore.pushValidationErrors(si$.value.$errors);
  }

  async function validateStepSession() {
    const createTrainingStore = useCreateTrainingStore();
    const { training } = storeToRefs(createTrainingStore);

    const rules = computed(() => addSessionRules(training.value.sessions));
    const si$ = useVuelidate(rules, training.value);
    await si$.value.$validate();

    createTrainingStore.pushValidationErrors(si$.value.$errors);
  }

  return {
    validateStepSessionItem,
    validateStepGeneral,
    validateStepContent,
    validateStepSession,
  };
};

export const useTrainingBuilderValidation = () => {
  async function validateStepContent() {
    const createBuilderTrainingStore = useCreateBuilderTrainingStore();
    const { training } = storeToRefs(createBuilderTrainingStore);

    const rules = computed(() => {
      return {
        content: {
          trainingPartner: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
          title: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
        },
      };
    });
    const si$ = useVuelidate(rules, training.value);
    await si$.value.$validate();

    createBuilderTrainingStore.pushValidationErrors(si$.value.$errors);
  }
  async function validateStepContentFull() {
    const createBuilderTrainingStore = useCreateBuilderTrainingStore();
    const { training } = storeToRefs(createBuilderTrainingStore);

    const rules = computed(() => {
      return {
        content: {
          trainingPartner: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
          title: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
          language: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
        },
      };
    });
    const si$ = useVuelidate(rules, training.value);
    await si$.value.$validate();

    createBuilderTrainingStore.pushValidationErrors(si$.value.$errors);
  }
  async function validateStepModality() {
    const createBuilderTrainingStore = useCreateBuilderTrainingStore();
    const { training } = storeToRefs(createBuilderTrainingStore);

    const rules = computed(() => {
      return {
        modalities: {
          durationDays: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
        },
      };
    });
    const si$ = useVuelidate(rules, training.value);
    await si$.value.$validate();

    createBuilderTrainingStore.pushValidationErrors(si$.value.$errors);
  }
  /**
   * A session's dates come from its steps, never from the draft itself — `createSession` sends the
   * timeslots and the backend spans the session over them. What is asked here is what the wizard's
   * first tab holds, exactly as on an external training.
   */
  async function validateStepSession() {
    const createBuilderTrainingStore = useCreateBuilderTrainingStore();
    const { training } = storeToRefs(createBuilderTrainingStore);

    const rules = computed(() => addSessionRules(training.value.sessions));
    const si$ = useVuelidate(rules, training.value);
    await si$.value.$validate();

    createBuilderTrainingStore.pushValidationErrors(si$.value.$errors);
  }
  async function validateStepAdvanced() {
    const createBuilderTrainingStore = useCreateBuilderTrainingStore();
    const { training } = storeToRefs(createBuilderTrainingStore);

    const rules = computed(() => {
      return {
        advanced: {
          categories: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
          ...(training.value.advanced.visibility.visible === VisibleStatus.GROUPS &&
            !training.value.advanced.visibility.groups.length && {
              visibility: {
                groups: {
                  required: helpers.withMessage("Ce champ est obligatoire", required),
                },
              },
            }),
        },
      };
    });
    const si$ = useVuelidate(rules, training.value);
    await si$.value.$validate();

    createBuilderTrainingStore.pushValidationErrors(si$.value.$errors);
  }

  async function validateStepContentForOneElement(element: string, tab: string) {
    const createBuilderTrainingStore = useCreateBuilderTrainingStore();
    const { training } = storeToRefs(createBuilderTrainingStore);

    const rules = computed(() => {
      return {
        [tab]: {
          [element]: {
            required: helpers.withMessage("Ce champ est obligatoire", required),
          },
        },
      };
    });
    const si$ = useVuelidate(rules, training.value);
    await si$.value.$validate();

    createBuilderTrainingStore.pushValidationErrors(si$.value.$errors);

    if (!si$.value.$errors.find((err) => err.$property === element)) {
      delete training.value.errors[`${tab}.${element}`];
    }
  }

  async function validateStepForVisibility() {
    const createBuilderTrainingStore = useCreateBuilderTrainingStore();
    const { training } = storeToRefs(createBuilderTrainingStore);

    const rules = computed(() => {
      return {
        advanced: {
          ...(training.value.advanced.visibility.visible === VisibleStatus.GROUPS &&
            !training.value.advanced.visibility.groups.length && {
              visibility: {
                groups: {
                  required: helpers.withMessage("Ce champ est obligatoire", required),
                },
              },
            }),
        },
      };
    });
    const si$ = useVuelidate(rules, training.value);
    await si$.value.$validate();

    createBuilderTrainingStore.pushValidationErrors(si$.value.$errors);
  }

  return {
    validateStepForVisibility,
    validateStepContentForOneElement,
    validateStepModality,
    validateStepContent,
    validateStepContentFull,
    validateStepSession,
    validateStepAdvanced,
  };
};
